/**
 * Mobile experience kill-switch.
 *
 * The new mobile-native flows (onboarding + My Drafts) render ONLY when this
 * returns true. It gates on a reversible flag so the feature can ship dark and
 * be toggled without a deploy, and so desktop behaviour is provably untouched
 * (every call site sits inside an existing `mobileView` branch).
 *
 * Resolution order:
 *   1. localStorage 'jd_mobile' = '1' | 'true'  -> force ON  (per-device, for QA)
 *   2. localStorage 'jd_mobile' = '0' | 'false' -> force OFF (per-device kill)
 *   3. build-time REACT_APP_MOBILE_EXPERIENCE === 'on' -> ON. This is the PROD
 *      rollout switch and the ONLY thing that can turn the feature on in a
 *      production build.
 *   4. STAGING builds (REACT_APP_ENV === 'staging') served from a recognised
 *      non-production host -> default ON, so the whole team can test on real
 *      phones at go.staging.jeddle.com with no per-device setup. jd_mobile='0'
 *      still kills it on one device.
 *   5. otherwise OFF
 *
 * @param {string} [hostname] overrides the serving host; for tests only.
 */
export function isMobileExperienceFlagOn(hostname) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const override = window.localStorage.getItem('jd_mobile');
      if (override === '1' || override === 'true') return true;
      if (override === '0' || override === 'false') return false;
    }
  } catch (e) {
    // Private mode / disabled storage — fall through to build default.
  }
  // The explicit rollout switch always wins — that is how prod turns it on.
  if (process.env.REACT_APP_MOBILE_EXPERIENCE === 'on') return true;
  // The IMPLICIT staging default must never be able to light up production, so
  // it needs two independent signals to agree: the build says staging AND the
  // page is actually being served from a known non-production host.
  return (
    process.env.REACT_APP_ENV === 'staging' && isNonProductionHost(hostname)
  );
}

// Hosts allowed to take the IMPLICIT staging default. This is an ALLOW-list on
// the real serving host, not a deny-list of production origins, because a
// deny-list only protects the origins someone remembered to enumerate: any new,
// renamed, or unrecognised production target would fail OPEN and quietly ship
// the mobile experience to real users. Here anything unlisted fails CLOSED.
//
// REACT_APP_ENV alone is not enough — it is set in the Netlify dashboard, not in
// the repo (`npm run build` is a plain `parcel build` and sets nothing), so a
// dashboard typo or a copied env block would otherwise be all it takes.
//
// Cost of being strict: a new staging URL loses the default-on convenience until
// it is added here (or a tester sets localStorage jd_mobile=1, or the build sets
// REACT_APP_MOBILE_EXPERIENCE=on). That is the cheap failure; the other one is not.
const NON_PRODUCTION_HOSTS = [
  'localhost',
  '127.0.0.1',
  'go.staging.jeddle.com',
];
// Netlify staging site + its branch/deploy previews (develop--jeddai-staging…).
// Production lives on jocular-taiyaki-3666a5.netlify.app, which this never matches.
const NON_PRODUCTION_HOST_SUFFIX = 'jeddai-staging.netlify.app';

function currentHostname() {
  try {
    return typeof window !== 'undefined' && window.location
      ? String(window.location.hostname || '')
      : '';
  } catch (e) {
    return '';
  }
}

/**
 * True only for hosts we positively recognise as non-production.
 * @param {string} [hostname] defaults to the real serving host; the parameter
 *   exists so tests can exercise other hosts without stubbing window.location.
 */
export function isNonProductionHost(hostname) {
  const host = (hostname ?? currentHostname()).toLowerCase();
  if (!host) return false; // unknown host -> fail closed
  return (
    NON_PRODUCTION_HOSTS.includes(host) ||
    host === NON_PRODUCTION_HOST_SUFFIX ||
    host.endsWith(`--${NON_PRODUCTION_HOST_SUFFIX}`)
  );
}

// A `useMobileExperience()` hook used to live here, returning
// { mobileView, enabled }. It is gone: it had no callers anywhere in the app,
// and it was a SECOND copy of the surface predicate that App already owns via
// useMobileHandoffView(). Two copies of that decision can only drift, and the
// dead one drifting is invisible. The single source of truth is
// components/ReactiveRender: isMobileView() for the raw viewport fact,
// useMobileHandoffView() for the surface decision (which also holds the mobile
// tree mounted across a resize on write routes — something the deleted hook
// never did, so any caller adopting it would have silently lost that).
// This module keeps only the flag helpers above.
